import React, { Suspense, useEffect, useRef, useState } from "react";
import { Button, Form, Input, InputNumber, Select, Switch, Space, Tag, Tooltip, Modal, Typography, Transfer } from "antd";
import { ContrastTag } from "../../../../components/shared";
import { PlusOutlined, BellOutlined, EditOutlined, DeleteOutlined, TeamOutlined, SyncOutlined, BranchesOutlined, CheckCircleOutlined, RobotOutlined, SafetyCertificateOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { getSessionUser } from "../../../../utils/auth/sessionUser";

// Shared components
import PageToolbar from "../../../../components/shared/PageToolbar";
import DataTable from "../../../../components/shared/DataTable";
import DetailDrawer from "../../../../components/shared/DetailDrawer";
import InlineLoading from "../../../../components/shared/InlineLoading";
import EmptyState from "../../../../components/shared/EmptyState";
import StatusTag from "../../../../components/shared/StatusTag";

// API
import { fetchAppointmentTypes, createAppointmentType, updateAppointmentType, deleteAppointmentType, assignPractitionersToType, removePractitionersFromType, fetchAvailableConfirmationTemplates, ConfirmationTemplate } from "../../../../api/healthcare/Appointment/AppointmentSettingsApi";
import { fetchTeamMembers } from "../../../../api/admin/teamApi";
import { fetchWorkflowTemplates, fetchAppointmentTypeWorkflows, assignWorkflowToAppointmentType, updateWorkflowAssignment, removeWorkflowAssignment } from "../../../../api/healthcare/Consultation/ConsultApi";

// Types
import { AppointmentTypeCreateRequest, AppointmentTypeUpdateRequest } from "../../../../types/appointments/AppointmentSettingsTypes";
import { TeamMember } from "../../../../types/admin/TeamTypes";
import { AppointmentType } from "../../../../types/appointments/AppointmentTypes";

import { fetchOverridesByAppointmentType } from "../../../../api/healthcare/PractitionerWorkflowApi";
import PractitionerWorkflowsTab from "./components/PractitionerWorkflowsTab";
import FollowUpNotificationsTab from "./components/FollowUpNotificationsTab";
import ReminderNotificationsTab from "./components/ReminderNotificationsTab";
import TandemRulesTab from "./components/TandemRulesTab";
import type { ScreeningQuestionsTabHandle } from "./screening/ScreeningQuestionsTab";

// Lazy so the screening API module — which reads `import.meta.env` — is only evaluated when the
// tab is actually opened, keeping this file importable under the CommonJS jest transform.
const ScreeningQuestionsTab = React.lazy(() => import("./screening/ScreeningQuestionsTab"));

// Hooks
import { useNotifications } from "../../../../context/NotificationContext";
import type { UserUuid } from "../../../../types/identity";

const { Text } = Typography;
const { confirm } = Modal;

const AppointmentTypesTab: React.FC = () => {
	const [types, setTypes] = useState<AppointmentType[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
	const [editingType, setEditingType] = useState<AppointmentType | null>(null);
	const [form] = Form.useForm();
	const [activeTab, setActiveTab] = useState<string>("details");
	const [allPractitioners, setAllPractitioners] = useState<TeamMember[]>([]);
	const [assignedPractitionerIds, setAssignedPractitionerIds] = useState<UserUuid[]>([]);
	const [practitionersLoading, setPractitionersLoading] = useState<boolean>(false);

	// Workflow state
	const [allWorkflows, setAllWorkflows] = useState<any[]>([]);
	const [workflowAssignments, setWorkflowAssignments] = useState<any[]>([]);
	const [workflowsLoading, setWorkflowsLoading] = useState<boolean>(false);
	const [workflowModalVisible, setWorkflowModalVisible] = useState<boolean>(false);
	const [selectedWorkflowId, setSelectedWorkflowId] = useState<number | null>(null);
	const [autoGenerate, setAutoGenerate] = useState<boolean>(false);

	const [confirmationTemplates, setConfirmationTemplates] = useState<ConfirmationTemplate[]>([]);

	const [practitionerOverrides, setPractitionerOverrides] = useState<Record<UserUuid, any[]>>({});
	const [loadingOverrides, setLoadingOverrides] = useState<boolean>(false);

	const screeningRef = useRef<ScreeningQuestionsTabHandle>(null);
	const [screeningSaving, setScreeningSaving] = useState<boolean>(false);

	const { showSuccess, showError } = useNotifications();
	const user = getSessionUser();

	useEffect(() => {
		loadTypes();
		loadPractitioners();
		loadWorkflows();
		loadConfirmationTemplates();
	}, []);

	const loadTypes = async () => {
		setLoading(true);
		try {
			const data = await fetchAppointmentTypes();
			setTypes(data);
		} catch (error) {
			showError("Failed to load appointment types");
		} finally {
			setLoading(false);
		}
	};

	const loadPractitionerOverrides = async (appointmentTypeId: number) => {
		setLoadingOverrides(true);
		try {
			const overrides = await fetchOverridesByAppointmentType(appointmentTypeId);
			setPractitionerOverrides(overrides);
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to load practitioner overrides");
		} finally {
			setLoadingOverrides(false);
		}
	};

	const loadPractitioners = async () => {
		setPractitionersLoading(true);
		try {
			const result = await fetchTeamMembers(1, 100);
			setAllPractitioners(result.data);
		} catch (error) {
			showError("Failed to load practitioners");
		} finally {
			setPractitionersLoading(false);
		}
	};

	const loadWorkflows = async () => {
		try {
			const response = await fetchWorkflowTemplates();
			const workflows = (response.templates || []).filter((w: any) => w.is_active);
			setAllWorkflows(workflows);
		} catch (error) {
			showError("Failed to load workflows");
		}
	};

	const loadConfirmationTemplates = async () => {
		try {
			const templates = await fetchAvailableConfirmationTemplates();
			setConfirmationTemplates(Array.isArray(templates) ? templates : []);
		} catch (error) {
			showError("Failed to load confirmation templates");
		}
	};

	const loadWorkflowAssignments = async (appointmentTypeId: number) => {
		setWorkflowsLoading(true);
		try {
			const response = await fetchAppointmentTypeWorkflows(appointmentTypeId);
			setWorkflowAssignments(response.data || []);
		} catch (error) {
			showError("Failed to load workflow assignments");
		} finally {
			setWorkflowsLoading(false);
		}
	};

	const openDrawer = (type?: AppointmentType) => {
		if (type) {
			setEditingType(type);
			form.setFieldsValue({
				name: type.name,
				description: type.description,
				duration_minutes: type.duration_minutes,
				color: type.color,
				is_active: type.is_active,
				allow_online_booking: type.allow_online_booking,
				requires_confirmation: type.requires_confirmation,
				default_price: type.default_price,
				max_bookings_per_patient: type.max_bookings_per_patient ?? null,
				confirmation_notification_template_id: type.confirmation_notification_template_id ?? null,
				telehealth_enabled: type.telehealth_enabled,
				category: type.category,
			});
			setAssignedPractitionerIds(type.practitioners?.map(p => p.id) || []);
			loadWorkflowAssignments(type.id);
			loadPractitionerOverrides(type.id);
		} else {
			setEditingType(null);
			form.resetFields();
			setAssignedPractitionerIds([]);
			setWorkflowAssignments([]);
		}
		setActiveTab("details");
		setDrawerVisible(true);
	};

	const closeDrawer = () => {
		setDrawerVisible(false);
		setEditingType(null);
		form.resetFields();
		setAssignedPractitionerIds([]);
		setActiveTab("details");
	};

	/**
	 * An empty cap must reach the wire as an explicit `null` (S10, ClickUp 86d3yhdwc).
	 *
	 * antd's InputNumber yields `null` when the user CLEARS it, but a field that was never
	 * touched comes back `undefined` — and `undefined` is dropped by JSON serialisation, so the
	 * key never arrives and a previously-set cap silently survives "clearing" it. `""` is no
	 * better: the backend validates `nullable|integer|min:1`, which 422s both `""` and `0`.
	 */
	const normaliseCap = (value: unknown): number | null =>
		value === null || value === undefined || value === "" ? null : Number(value);

	// Same reason as normaliseCap: antd's Select yields `undefined` when cleared, and `undefined` is
	// dropped by JSON serialisation — so "back to the clinic default" would silently not save.
	const normaliseTemplateId = (value: unknown): number | null =>
		value === null || value === undefined || value === "" ? null : Number(value);

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields();
			setLoading(true);

			const payload = {
				...values,
				max_bookings_per_patient: normaliseCap(values.max_bookings_per_patient),
				confirmation_notification_template_id: normaliseTemplateId(values.confirmation_notification_template_id),
			};

			if (editingType) {
				const updateData: AppointmentTypeUpdateRequest = payload;
				await updateAppointmentType(editingType.id, updateData);
				showSuccess("Appointment type updated successfully");
			} else {
				const createData: AppointmentTypeCreateRequest = { ...payload };
				await createAppointmentType(createData);
				showSuccess("Appointment type created successfully");
			}

			await loadTypes();
			closeDrawer();
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to save appointment type");
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = (type: AppointmentType) => {
		confirm({
			title: "Confirm Deletion",
			content: `Are you sure you want to delete "${type.name}"? This action cannot be undone.`,
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await deleteAppointmentType(type.id);
					showSuccess("Appointment type deleted successfully");
					await loadTypes();
				} catch (error: any) {
					showError(error.response?.data?.message || "Failed to delete appointment type");
				}
			},
		});
	};

	const handleScreeningSave = async () => {
		setScreeningSaving(true);
		try {
			await screeningRef.current?.save();
		} finally {
			setScreeningSaving(false);
		}
	};

	const handlePractitionersUpdate = async () => {
		if (!editingType) return;

		try {
			setPractitionersLoading(true);

			const currentPractitionerIds = editingType.practitioners?.map(p => p.id) || [];
			const practitionersToAdd = assignedPractitionerIds.filter(id => !currentPractitionerIds.includes(id));
			const practitionersToRemove = currentPractitionerIds.filter(id => !assignedPractitionerIds.includes(id));

			if (practitionersToAdd.length > 0) {
				await assignPractitionersToType(editingType.id, practitionersToAdd);
			}

			if (practitionersToRemove.length > 0) {
				await removePractitionersFromType(editingType.id, practitionersToRemove);
			}

			showSuccess("Practitioners updated successfully");
			await loadTypes();
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to update practitioners");
		} finally {
			setPractitionersLoading(false);
		}
	};

	// Workflow handlers
	const handleOpenWorkflowModal = () => {
		setSelectedWorkflowId(null);
		setAutoGenerate(false);
		setWorkflowModalVisible(true);
	};

	const handleAssignWorkflow = async () => {
		if (!editingType || !selectedWorkflowId) return;

		try {
			setWorkflowsLoading(true);
			await assignWorkflowToAppointmentType(editingType.id, selectedWorkflowId, true, autoGenerate);
			showSuccess("Workflow assigned successfully");
			setWorkflowModalVisible(false);
			await loadWorkflowAssignments(editingType.id);
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to assign workflow");
		} finally {
			setWorkflowsLoading(false);
		}
	};

	const handleToggleWorkflowActive = async (assignmentId: number, isActive: boolean) => {
		if (!editingType) return;

		try {
			setWorkflowsLoading(true);
			await updateWorkflowAssignment(editingType.id, assignmentId, isActive, undefined);
			showSuccess("Workflow updated successfully");
			await loadWorkflowAssignments(editingType.id);
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to update workflow");
		} finally {
			setWorkflowsLoading(false);
		}
	};

	const handleToggleAutoGenerate = async (assignmentId: number, autoGen: boolean) => {
		if (!editingType) return;

		try {
			setWorkflowsLoading(false);
			await updateWorkflowAssignment(editingType.id, assignmentId, undefined, autoGen);
			showSuccess("Auto-generate updated successfully");
			await loadWorkflowAssignments(editingType.id);
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to update auto-generate");
		} finally {
			setWorkflowsLoading(false);
		}
	};

	const handleRemoveWorkflow = async (assignmentId: number) => {
		if (!editingType) return;

		confirm({
			title: "Remove Workflow?",
			content: "Are you sure you want to remove this workflow assignment?",
			okText: "Remove",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					setWorkflowsLoading(true);
					await removeWorkflowAssignment(editingType!.id, assignmentId);
					showSuccess("Workflow removed successfully");
					await loadWorkflowAssignments(editingType!.id);
				} catch (error: any) {
					showError(error.response?.data?.message || "Failed to remove workflow");
				} finally {
					setWorkflowsLoading(false);
				}
			},
		});
	};

	const columns: ColumnsType<AppointmentType> = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			render: (name: string, record: AppointmentType) => (
				<Space>
					<div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: record.color }} />
					<Text strong>{name}</Text>
					{record.cliniko_id && (
						<ContrastTag background="var(--mg-info)" icon={<SyncOutlined />}>Synced</ContrastTag>
					)}
				</Space>
			),
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			ellipsis: true,
		},
		{
			title: "Duration",
			dataIndex: "duration_minutes",
			key: "duration",
			width: 100,
			render: (minutes: number) => `${minutes} min`,
		},
		{
			title: "Practitioners",
			key: "practitioners",
			width: 130,
			render: (_: any, record: AppointmentType) => (
				<Space>
					<TeamOutlined style={{ color: "var(--mg-text-secondary)" }} />
					<Text>{record.practitioners?.length || 0}</Text>
				</Space>
			),
		},
		{
			title: "Status",
			dataIndex: "is_active",
			key: "is_active",
			width: 100,
			render: (v: boolean) => <StatusTag status={v ? "active" : "inactive"} />,
		},
		{
			title: "",
			key: "actions",
			width: 80,
			render: (_: any, record: AppointmentType) => (
				<Space size={2}>
					<Tooltip title="Edit">
						<Button type="text" size="small" icon={<EditOutlined />} onClick={e => { e.stopPropagation(); openDrawer(record); }} />
					</Tooltip>
					<Tooltip title="Delete">
						<Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={e => { e.stopPropagation(); handleDelete(record); }} />
					</Tooltip>
				</Space>
			),
		},
	];

	// Build drawer tab items
	const drawerTabs = [
		{
			key: "details",
			label: "Details",
			children: (
				<Form form={form} layout="vertical">
					<Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter a name" }]}>
						<Input placeholder="e.g., General Consultation" />
					</Form.Item>

					<Form.Item label="Category" name="category">
						<Input placeholder="e.g., Physical Therapy, Consultation" />
					</Form.Item>

					<Form.Item label="Description" name="description">
						<Input.TextArea rows={3} placeholder="Brief description of this appointment type" />
					</Form.Item>

					<Form.Item label="Duration (Minutes)" name="duration_minutes" rules={[{ required: true, message: "Please enter duration" }]}>
						<InputNumber min={5} max={480} step={5} style={{ width: "100%" }} />
					</Form.Item>

					<Form.Item label="Color" name="color" rules={[{ required: true, message: "Please select a color" }]}>
						<Input type="color" style={{ width: 100, height: 32 }} />
					</Form.Item>

					<Form.Item label="Default Price" name="default_price">
						<InputNumber min={0} step={0.01} prefix="$" style={{ width: "100%" }} placeholder="Optional" />
					</Form.Item>

					{/*
						S10 (ClickUp 86d3yhdwc). Blank = unlimited: the value is normalised to an
						explicit `null` in handleSubmit(), never `0` or `""`, both of which the
						backend 422s. `min={1}` keeps a 0 out of the field in the first place.
					*/}
					<Form.Item
						label="Max bookings per patient"
						name="max_bookings_per_patient"
						extra="Leave blank for unlimited. Cancelled and no-show appointments do not count."
					>
						<InputNumber min={1} placeholder="Unlimited" style={{ width: "100%" }} />
					</Form.Item>

					<Form.Item label="Active" name="is_active" valuePropName="checked">
						<Switch />
					</Form.Item>

					<Form.Item label="Allow Online Booking" name="allow_online_booking" valuePropName="checked">
						<Switch />
					</Form.Item>

					<Form.Item label="Requires Confirmation" name="requires_confirmation" valuePropName="checked">
						<Switch />
					</Form.Item>

					{/* The list carries inactive templates on purpose — here `is_active` marks the clinic-wide
						DEFAULT, so without the suffix a practitioner cannot tell what leaving this blank gives them. */}
					<Form.Item
						label="Confirmation Email Template"
						name="confirmation_notification_template_id"
						extra="Leave blank to use the clinic's default confirmation template."
					>
						<Select
							allowClear
							disabled={confirmationTemplates.length === 0}
							placeholder={confirmationTemplates.length === 0 ? "No confirmation templates available" : "Use clinic default"}
							options={confirmationTemplates.map(t => ({
								value: t.id,
								label: t.is_active ? `${t.name} (Default)` : t.name,
							}))}
						/>
					</Form.Item>

					<Form.Item label="Enable Telehealth" name="telehealth_enabled" valuePropName="checked">
						<Switch />
					</Form.Item>

					{editingType?.cliniko_id && (
						<div style={{ marginTop: 16, padding: 12, borderRadius: 6, background: "var(--mg-bg-subtle)", border: "1px solid var(--mg-border)" }}>
							<Space direction="vertical" size="small">
								<Text strong>Cliniko Integration</Text>
								<Text type="secondary" style={{ fontSize: 12 }}>
									Cliniko ID: {editingType.cliniko_id}
								</Text>
								{editingType.cliniko_synced_at && (
									<Text type="secondary" style={{ fontSize: 12 }}>
										Last synced: {dayjs(editingType.cliniko_synced_at).format("DD/MM/YYYY HH:mm")}
									</Text>
								)}
							</Space>
						</div>
					)}
				</Form>
			),
		},
		{
			key: "practitioners",
			label: `Practitioners${editingType ? ` (${assignedPractitionerIds.length})` : ""}`,
			disabled: !editingType,
			children: !editingType ? (
				<EmptyState title="Save first" description="Please save the appointment type before assigning practitioners" />
			) : (
				<div>
					<div style={{ marginBottom: 16 }}>
						<Text strong style={{ fontSize: 14 }}>Assign Practitioners</Text>
						<div style={{ marginTop: 4 }}>
							<Text type="secondary" style={{ fontSize: 12 }}>
								Select which practitioners can provide this appointment type
							</Text>
						</div>
					</div>
					<div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
						<Button type="default" onClick={handlePractitionersUpdate} loading={practitionersLoading}>
							Update Practitioners
						</Button>
					</div>
					{practitionersLoading ? (
						<InlineLoading minHeight={200} message="Loading practitioners…" />
					) : (
						<Transfer
							dataSource={allPractitioners}
							targetKeys={assignedPractitionerIds}
							onChange={targetKeys => setAssignedPractitionerIds(targetKeys.map(k => String(k)))}
							rowKey={record => record.id}
							render={item => (
								<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
									<div style={{
										width: 8,
										height: 8,
										borderRadius: "50%",
										backgroundColor: item.is_active ? "var(--mg-success)" : "var(--mg-border)",
									}} />
									<span>{`${item.first_name} ${item.last_name}`}</span>
									{item.team_member_info?.specialties?.length > 0 && (
										<Text type="secondary" style={{ fontSize: 11 }}>
											({item.team_member_info.specialties[0]})
										</Text>
									)}
								</div>
							)}
							titles={["Available Practitioners", "Assigned Practitioners"]}
							showSearch
							filterOption={(inputValue, item) =>
								`${item.first_name} ${item.last_name}`.toLowerCase().includes(inputValue.toLowerCase()) ||
								(item.team_member_info?.specialties || []).some((s: string) => s.toLowerCase().includes(inputValue.toLowerCase()))
							}
							style={{ width: "100%" }}
							listStyle={{ width: "100%", height: 400 }}
						/>
					)}
				</div>
			),
		},
		{
			key: "workflows",
			label: (
				<span>
					<BranchesOutlined /> Workflows{editingType && workflowAssignments.length > 0 ? ` (${workflowAssignments.length})` : ""}
				</span>
			),
			disabled: !editingType,
			children: !editingType ? (
				<EmptyState title="Save first" description="Please save the appointment type before assigning workflows" />
			) : (
				<div>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
						<div>
							<Text strong style={{ fontSize: 14 }}>Assigned Workflows</Text>
							<div style={{ marginTop: 4 }}>
								<Text type="secondary" style={{ fontSize: 12 }}>
									Workflows that generate consultation guidance for this appointment type
								</Text>
							</div>
						</div>
						<Button type="primary" icon={<PlusOutlined />} onClick={handleOpenWorkflowModal} disabled={workflowsLoading}>
							Assign Workflow
						</Button>
					</div>

					{workflowsLoading ? (
						<InlineLoading minHeight={160} message="Loading workflows…" />
					) : workflowAssignments.length === 0 ? (
						<EmptyState
							title="No workflows assigned"
							description='Click "Assign Workflow" to add consultation guidance automation'
							action={{ label: "Assign Workflow", icon: <PlusOutlined />, onClick: handleOpenWorkflowModal }}
						/>
					) : (
						<Space direction="vertical" style={{ width: "100%" }} size={12}>
							{workflowAssignments.map((assignment: any) => (
								<div
									key={assignment.id}
									style={{
										border: "1px solid var(--mg-border)",
										borderRadius: 8,
										padding: "12px 16px",
										background: "var(--mg-bg-elevated)",
									}}
								>
									<div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
										<div style={{ flex: 1 }}>
											<div style={{ marginBottom: 8 }}>
												<Text strong>{assignment.workflow_template?.name || "Unnamed Workflow"}</Text>
												<Space style={{ marginLeft: 8 }}>
													<StatusTag status={assignment.is_active ? "active" : "inactive"} />
													{assignment.auto_generate && (
														<ContrastTag icon={<RobotOutlined />} background="var(--mg-info)">Auto-Generate</ContrastTag>
													)}
												</Space>
											</div>
											{assignment.workflow_template?.description && (
												<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
													{assignment.workflow_template.description}
												</Text>
											)}
											<Space size="large">
												<Text type="secondary" style={{ fontSize: 11 }}>
													Version: <Text style={{ fontSize: 11 }}>v{assignment.workflow_template?.version || 1}</Text>
												</Text>
												<Text type="secondary" style={{ fontSize: 11 }}>
													Steps: <Text style={{ fontSize: 11 }}>{assignment.workflow_template?.steps?.length || 0}</Text>
												</Text>
											</Space>
										</div>
										<Space direction="vertical" size="small">
											<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
												<Text style={{ fontSize: 11 }}>Active:</Text>
												<Switch size="small" checked={assignment.is_active} onChange={checked => handleToggleWorkflowActive(assignment.id, checked)} disabled={workflowsLoading} />
											</div>
											<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
												<Tooltip title="Automatically generate consultation guidance when appointment is created">
													<Text style={{ fontSize: 11 }}>Auto-gen:</Text>
												</Tooltip>
												<Switch
													size="small"
													checked={assignment.auto_generate}
													onChange={checked => handleToggleAutoGenerate(assignment.id, checked)}
													disabled={workflowsLoading || !assignment.is_active}
												/>
											</div>
											<Button danger size="small" icon={<DeleteOutlined />} onClick={() => handleRemoveWorkflow(assignment.id)} disabled={workflowsLoading}>
												Remove
											</Button>
										</Space>
									</div>
								</div>
							))}
						</Space>
					)}
				</div>
			),
		},
		{
			key: "practitioner-workflows",
			label: (
				<span><TeamOutlined /> Practitioner Workflows</span>
			),
			disabled: !editingType,
			children: !editingType ? (
				<EmptyState title="Save first" description="Please save the appointment type first" />
			) : (
				<PractitionerWorkflowsTab
					appointmentType={editingType}
					practitioners={editingType.practitioners || []}
					allWorkflows={allWorkflows}
					overrides={practitionerOverrides}
					onRefresh={() => loadPractitionerOverrides(editingType.id)}
					loading={loadingOverrides}
				/>
			),
		},
		{
			key: "follow-ups",
			label: <span><BellOutlined /> Follow-up Notifications</span>,
			disabled: !editingType,
			children: !editingType ? (
				<EmptyState title="Save first" description="Please save the appointment type first" />
			) : (
				<FollowUpNotificationsTab appointmentTypeId={editingType.id} />
			),
		},
		{
			key: "reminders",
			label: <span><BellOutlined style={{ color: "var(--mg-primary)" }} /> Reminder Notifications</span>,
			disabled: !editingType,
			children: !editingType ? (
				<EmptyState title="Save first" description="Please save the appointment type first" />
			) : (
				<ReminderNotificationsTab appointmentTypeId={editingType.id} />
			),
		},
		{
			key: "screening",
			label: <span><SafetyCertificateOutlined /> Screening Questions</span>,
			disabled: !editingType,
			children: !editingType ? (
				<EmptyState title="Save first" description="Please save the appointment type first" />
			) : (
				<Suspense fallback={<InlineLoading minHeight={200} message="Loading screening questions…" />}>
					<ScreeningQuestionsTab
						ref={screeningRef}
						appointmentTypeId={editingType.id}
						blockMessage={editingType.screening_block_message}
						onSaved={loadTypes}
					/>
				</Suspense>
			),
		},
		{
			key: "tandem",
			label: <span><UsergroupAddOutlined /> Tandem Rules</span>,
			disabled: !editingType,
			children: !editingType ? (
				<EmptyState title="Save first" description="Please save the appointment type first" />
			) : (
				<TandemRulesTab
					appointmentTypeId={editingType.id}
					assignedPractitioners={allPractitioners.filter(p => assignedPractitionerIds.includes(p.id))}
				/>
			),
		},
	].filter(tab => !tab.disabled || editingType);

	return (
		<>
			<PageToolbar
				primaryAction={{
					label: "Add Type",
					icon: <PlusOutlined />,
					onClick: () => openDrawer(),
				}}
			/>

			<DataTable
				//title="Appointment Types"
				columns={columns}
				dataSource={types}
				rowKey="id"
				loading={loading}
				onRowClick={type => openDrawer(type)}
				emptyTitle="No appointment types found"
				emptyDescription="Create your first appointment type to get started"
				emptyAction={{ label: "Add Type", onClick: () => openDrawer() }}
			/>

			{/* Configuration Drawer */}
			<DetailDrawer
				open={drawerVisible}
				onClose={closeDrawer}
				width="60vw"
				title={editingType ? editingType.name : "Add Appointment Type"}
				subtitle={editingType?.category || undefined}
				headerActions={
					activeTab === "details" ? (
						<Button type="primary" onClick={handleSubmit} loading={loading}>
							{editingType ? "Update" : "Create"}
						</Button>
					) : activeTab === "practitioners" && editingType ? (
						<Button type="default" onClick={handlePractitionersUpdate} loading={practitionersLoading}>
							Update Practitioners
						</Button>
					) : activeTab === "screening" && editingType ? (
						<Button type="default" onClick={handleScreeningSave} loading={screeningSaving}>
							Save Screening
						</Button>
					) : undefined
				}
				tabs={drawerTabs}
				activeTab={activeTab}
				onTabChange={setActiveTab}
			/>

			{/* Workflow Assignment Modal */}
			<Modal
				title="Assign Workflow"
				open={workflowModalVisible}
				onCancel={() => setWorkflowModalVisible(false)}
				onOk={handleAssignWorkflow}
				okText="Assign"
				okButtonProps={{ disabled: !selectedWorkflowId, loading: workflowsLoading }}
				width={600}
				destroyOnClose
			>
				<Space direction="vertical" style={{ width: "100%" }} size="middle">
					<div>
						<Text strong>Select Workflow</Text>
						<Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
							Choose a consultation guidance workflow to assign to this appointment type
						</Text>
					</div>

					<Space direction="vertical" style={{ width: "100%" }}>
						{allWorkflows.length === 0 ? (
							<EmptyState title="No active workflows" description="Create workflows in the Consultation Workflows tab" />
						) : (
							allWorkflows
								.filter(w => !workflowAssignments.some(a => a.workflow_template_id === w.id))
								.map((workflow: any) => (
									<div
										key={workflow.id}
										style={{
											cursor: "pointer",
											border: selectedWorkflowId === workflow.id ? "2px solid var(--mg-primary)" : "1px solid var(--mg-border)",
											background: selectedWorkflowId === workflow.id ? "var(--mg-primary-bg)" : "transparent",
											borderRadius: 8,
											padding: "12px 16px",
										}}
										onClick={() => setSelectedWorkflowId(workflow.id)}
									>
										<div style={{ display: "flex", alignItems: "start", justifyContent: "space-between" }}>
											<div style={{ flex: 1 }}>
												<div style={{ marginBottom: 4 }}>
													<Text strong>{workflow.name}</Text>
													<ContrastTag style={{ marginLeft: 8 }} background="var(--mg-info)">v{workflow.version}</ContrastTag>
												</div>
												{workflow.description && (
													<Text type="secondary" style={{ fontSize: 12 }}>
														{workflow.description}
													</Text>
												)}
												<div style={{ marginTop: 8 }}>
													<Text type="secondary" style={{ fontSize: 11 }}>
														{workflow.steps?.length || 0} steps
													</Text>
												</div>
											</div>
											{selectedWorkflowId === workflow.id && (
												<CheckCircleOutlined style={{ fontSize: 20, color: "var(--mg-primary)" }} />
											)}
										</div>
									</div>
								))
						)}
					</Space>

					{selectedWorkflowId && (
						<div style={{ marginTop: 8 }}>
							<Space align="start">
								<Switch checked={autoGenerate} onChange={setAutoGenerate} />
								<div>
									<Text>Auto-generate on appointment creation</Text>
									<br />
									<Text type="secondary" style={{ fontSize: 11 }}>
										Automatically generate consultation guidance when an appointment is created
									</Text>
								</div>
							</Space>
						</div>
					)}
				</Space>
			</Modal>
		</>
	);
};

export default AppointmentTypesTab;
